/**
 * Frontend usage-event tracking (issue #1450, foundation for epic #1448's
 * DAU/WAU/MAU adoption signal).
 *
 * Posts to the platform-usage ingestion endpoint from issue #1449
 * (`POST /api/v1/platform-usage/events`, `backend/src/validation/platformUsageSchemas.ts`).
 * `userId` is derived server-side from the authenticated caller — never sent
 * in the payload.
 *
 * Best-effort by design, mirroring the backend's own `PlatformUsageEventRepository`
 * write path (`backend/src/utils/metrics.ts#L20-L51`'s `publishMetric` contract):
 * a usage-tracking outage must never throw into, or block, the user action
 * being tracked.
 *
 * Every call carries the current tab's session id as an `X-Session-Id`
 * header — on this usage-event batch call only, not on every API call —
 * the same header-based, generated-once-and-carried propagation shape
 * `X-Correlation-ID` already uses server-side
 * (`backend/src/middleware/correlationId.ts`).
 */

import { apiRequest } from './apiClient/http';
import { getSessionId } from './usageSession';

export interface TrackUsageEventOptions {
  teamId?: string;
  metadata?: Record<string, unknown>;
}

interface PlatformUsageEventPayload {
  eventType: string;
  timestamp: string;
  teamId?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Emits a single usage event through the batch-ingestion endpoint. Never
 * throws — failures are logged and swallowed.
 */
export async function trackUsageEvent(
  eventType: string,
  options: TrackUsageEventOptions = {}
): Promise<void> {
  const event: PlatformUsageEventPayload = {
    eventType,
    timestamp: new Date().toISOString(),
    ...(options.teamId ? { teamId: options.teamId } : {}),
    ...(options.metadata ? { metadata: options.metadata } : {}),
  };

  const sessionId = getSessionId();

  try {
    await apiRequest('/platform-usage/events', {
      method: 'POST',
      headers: sessionId ? { 'X-Session-Id': sessionId } : {},
      body: JSON.stringify({ events: [event] }),
    });
  } catch (error) {
    // Best-effort — never propagate a tracking failure to the caller.
    console.error('trackUsageEvent: failed to record event', eventType, error);
  }
}
